/**
 * current-team.ts — who "you" are, resolved in one place.
 *
 * Components and domains used to find the viewer by hand, each in its own
 * spelling: session → user id → playersData[id].cohort_id → teams[cohortId].
 * The Vesper redesign draws the viewer's own team differently from everyone
 * else's, so that question needs one answer, and this is it.
 *
 * Hooks are for components. The get* functions read the stores once, for
 * message handlers and other code that runs outside React.
 *
 * design-checks' `current-team-lookup` rule holds hand-rolled lookups of the
 * viewer at zero everywhere but here.
 */
import { useAuthStore } from './auth';
import { usePlayersStore, type OnlinePlayerData } from './players';
import { useTeamsStore, type Team } from './teams';

/** The viewer's user id, or undefined before a session exists. */
export function getViewerUserId(): string | undefined {
  return useAuthStore.getState().session?.user_id;
}

/** Reactive {@link getViewerUserId}. */
export function useViewerUserId(): string | undefined {
  return useAuthStore((s) => s.session?.user_id);
}

/** The viewer's own player record — undefined until the player list arrives. */
export function getCurrentPlayer(): OnlinePlayerData | undefined {
  const userId = getViewerUserId();
  return userId ? usePlayersStore.getState().playersData[userId] : undefined;
}

/** Reactive {@link getCurrentPlayer}. */
export function useCurrentPlayer(): OnlinePlayerData | undefined {
  const userId = useViewerUserId();
  return usePlayersStore((s) => (userId ? s.playersData[userId] : undefined));
}

/** The viewer's own budget, in millions — undefined until the server has sent the team's budgets. */
export function useViewerBudget(): number | undefined {
  const userId = useViewerUserId();
  return usePlayersStore((s) => (userId ? s.budgets[userId] : undefined));
}

/** The viewer's cohort id — the key of their team in the teams store. */
export function getCurrentCohortId(): string | undefined {
  return getCurrentPlayer()?.cohort_id;
}

/** Reactive {@link getCurrentCohortId}. */
export function useCurrentCohortId(): string | undefined {
  return useCurrentPlayer()?.cohort_id;
}

/** The viewer's team — undefined until both the player list and the teams have arrived. */
export function getCurrentTeam(): Team | undefined {
  const cohortId = getCurrentCohortId();
  return cohortId ? useTeamsStore.getState().teams[cohortId] : undefined;
}

/** Reactive {@link getCurrentTeam}. */
export function useCurrentTeam(): Team | undefined {
  const cohortId = useCurrentCohortId();
  return useTeamsStore((s) => (cohortId ? s.teams[cohortId] : undefined));
}
